import { Component, HostListener, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { Event, NavigationEnd, Router } from "@angular/router";
import { UserIdleService } from "angular-user-idle";
import { Observable, Subscription, timer } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "../../core/authentication/authentication.service";
import { parseJwt } from "../../core/jwt/jwt.util";
import { NotificationService } from "../../shared/components/notification/notification.service";
import {
  SimpleDialogComponent,
  SimpleDialogProperties,
} from "../../shared/components/simple-dialog/simple-dialog.component";
import { UserInfo } from "../../shared/types/user-info";
import { LocalStorageKeys } from "src/app/core/storage/local-storage";

/** Silent refresh timer in seconds */
const SILENT_REFRESH_TIMER = environment.jwtApplyRefreshDelta;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  showNav: boolean;
  subscription: Subscription;
  currentUser: UserInfo;
  hostname: string;
  favIcon: HTMLLinkElement = document.querySelector("#favIcon");

  constructor(
    public router: Router,
    public dialog: MatDialog,
    public matIconRegistry: MatIconRegistry,
    private userIdleService: UserIdleService,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
  ) {
    matIconRegistry.registerFontClassAlias("fontawesome", "fa");
    router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.showNav = !(router.url.includes("/login") || router.url.includes("/forgot") || router.url === "/");
      }
    });
    this.hostname = window.location.hostname;
    if (this.hostname.endsWith("itdivest.relutech.com")) {
      this.favIcon.href = "../../../assets/relutech.ico";
    } else {
      this.favIcon.href = "../../../assets/favicon.ico";
    }
  }

  @HostListener("keydown")
  @HostListener("mouseover")
  checkUserActivity(): void {
    this.restartTimer();
  }

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserInfo;
    if (this.currentUser) {
      // Start watching for user inactivity.
      this.startWatching();
      // Start watching when user idle is starting.
      this.userIdleService.onTimerStart().subscribe();
      // Start watch when time is up.
      this.userIdleService.onTimeout().subscribe(() => {
        this.refreshSession();
      });
      this.subscription = this.userIdleService.ping$.subscribe(() => this.refreshSessionInSilence());
      if (environment.luckyOrangeUrl) {
        this.identifyLuckyOrangeVisitor(this.currentUser);
      }
    }
  }

  private refreshSession(): void {
    this.stopTimer();
    this.stopWatching();
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "refreshToken",
      panelClass: "custom-dialog-container-timeout",
      disableClose: true,
      data: {
        title: "Session is about to expire",
        message: "Do you want to continue with the session?",
        type: "yesNo",
        action: "refreshSession",
        timerEnabled: true,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result && this.currentUser) {
        this.refreshToken(this.currentUser.refreshToken);
        this.restartTimer();
        this.startWatching();
      } else {
        this.closeSession("Logging out");
      }
    });
  }

  private refreshSessionInSilence(): void {
    const user = this.authenticationService.currentUserInfo;
    try {
      const { accessToken, refreshToken } = user;
      const expiration = parseJwt(accessToken).secondsRemaining;
      // If the token is about to expire (< SILENT_REFRESH_TIMER) and the user is active
      // then the session will be refreshed
      if (expiration < SILENT_REFRESH_TIMER) {
        this.refreshToken(refreshToken);
      }
    } catch (error) {
      this.closeSession();
    }
  }

  private closeSession = (message?: string): void => {
    this.stopTimer();
    this.stopWatching();
    this.subscription.unsubscribe();

    if (this.currentUser?.refreshToken) {
      this.authenticationService.revokeRefreshToken(this.currentUser.refreshToken).subscribe();
    }

    this.authenticationService.logout();
    const error = message || "Unable to keep session, redirecting, wait a few seconds..";
    this.notificationService.error(error);
    this.delay(5000).subscribe(() => {
      this.router.navigate(["/logout"]);
    });
  };

  private startWatching(): void {
    this.userIdleService.startWatching();
  }

  private stopWatching(): void {
    this.userIdleService.stopWatching();
  }

  private restartTimer(): void {
    this.userIdleService.resetTimer();
  }

  private stopTimer(): void {
    this.userIdleService.stopTimer();
  }

  private refreshToken(refreshToken: string): void {
    this.authenticationService.refreshToken(refreshToken).subscribe(
      () => {
        this.notificationService.success("Session Renewed");
        this.currentUser = this.authenticationService.currentUserInfo;
      },
      (error) => {
        this.closeSession();
      },
    );
  }

  private async identifyLuckyOrangeVisitor(user: UserInfo): Promise<void> {
    if (localStorage.getItem(LocalStorageKeys.luckyOrangeIdentified)) {
      return;
    }
    window.LOQ = window.LOQ || [];
    window.LOQ.push([
      "ready",
      async (lo) => {
        await lo.$internal.ready("visitor");
        lo.visitor.identify(user.id, { email: user.email, name: user.fullName() });
        localStorage.setItem(LocalStorageKeys.luckyOrangeIdentified, "true");
      },
    ]);
  }

  private delay(seconds: number): Observable<number> {
    return timer(seconds);
  }
}
